import React, { useContext, useEffect, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, Form, Row, Col,
    FormGroup, Label, Input, Button
} from 'reactstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { AgendaContext } from '../../context/AgendaContext';
import { TipoAgendaContext } from '../../context/TipoAgendaContext';
import { TerceiroContext } from '../../context/TerceiroContext';
import { EditAgendaType } from '../../context/hooks/useAgenda';
import { AuthContext } from '../../context/AuthContext';
import { CursoContext } from '../../context/CursoContext';

interface Props {
    isOpen: boolean;
    toggle: () => void;
    agenda: EditAgendaType | null;
    onUpdated?: () => void;
    onDeleted?: () => void;
}

type Option = { value: number; label: string };

export const EditAgendaModal: React.FC<Props> = ({
    isOpen, toggle, agenda, onUpdated, onDeleted
}) => {
    const { updateAgenda, deleteAgenda, addCursoNaAgenda, removeCursoDaAgenda } = useContext(AgendaContext);
    const { listTipoAgenda, getAllTipoAgendaPaged } = useContext(TipoAgendaContext);
    const { listTerceiroByAgenda, handleGetTerceiroByAgendaId } = useContext(TerceiroContext);
    const { objUserTk } = useContext(AuthContext);
    const { handleGetCursoByEmpresaId, listCurso } = useContext(CursoContext);

    const [form, setForm] = useState<EditAgendaType | null>(agenda);

    type CursoBadge = { id: number; name: string };
    const [linkedCursos, setLinkedCursos] = useState<CursoBadge[]>([]);
    const [addingCursoBusy, setAddingCursoBusy] = useState(false);
    const [removingCursoBusy, setRemovingCursoBusy] = useState<number | null>(null);

    useEffect(() => {
        if (isOpen && agenda) {
            setForm(agenda);
            handleGetCursoByEmpresaId(objUserTk.EmpresaId);
            handleGetTerceiroByAgendaId(agenda.id);
            if (!listTipoAgenda.length) getAllTipoAgendaPaged(1, 1000, objUserTk.EmpresaId);

            setLinkedCursos([]);
        }
    }, [isOpen, agenda]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (!form) return;
        const { name, value } = e.target;
        setForm({ ...form, [name]: name === 'step' || name === 'tipoAgendaId' ? +value : value });
    };

    useEffect(() => {
        if (!isOpen || !agenda) return;

        const ids: number[] =
            (agenda.cursoIds && agenda.cursoIds.length
                ? agenda.cursoIds
                : (agenda.cursoId != null ? [agenda.cursoId] : [])
            ).filter((n): n is number => typeof n === 'number');

        const namesFromDto: string[] =
            ((agenda.cursoNames && agenda.cursoNames.length
                ? agenda.cursoNames
                : (agenda.cursoName ? [agenda.cursoName] : [])
            )).map(String);

        const resolved: CursoBadge[] = ids.map((id, idx) => {
            const fromList = listCurso.find(c => c.id === id)?.name;
            const fromDto = namesFromDto[idx] ?? namesFromDto.find(n => !!n);
            return { id, name: fromList || fromDto || `Curso #${id}` };
        });

        setLinkedCursos(resolved);

        setForm(prev => prev ? ({ ...prev, cursoIds: ids }) : prev);
    }, [isOpen, agenda, listCurso]);

    const listCursoOptions: Option[] = listCurso.map(curso => ({ value: curso.id, label: curso.name }));
    const linkedIds = new Set(linkedCursos.map(c => c.id));
    const addableCursoOptions = listCursoOptions.filter(o => !linkedIds.has(o.value));

    const handleAddCurso = async (opt: Option | null) => {
        if (!form || !opt) return;
        if (linkedIds.has(opt.value)) return;

        setAddingCursoBusy(true);
        try {
            const ok = await addCursoNaAgenda(form.id, opt.value);
            if (ok) {
                setLinkedCursos(prev => [...prev, { id: opt.value, name: opt.label }]);
                setForm(prev => prev ? { ...prev, cursoIds: [...(prev.cursoIds ?? []), opt.value] } : prev);
            }
        } finally {
            setAddingCursoBusy(false);
        }
    };

    const handleRemoveCurso = async (cursoId: number) => {
        if (!form) return;
        setRemovingCursoBusy(cursoId);
        try {
            const ok = await removeCursoDaAgenda(form.id, cursoId);
            if (ok) {
                setLinkedCursos(prev => prev.filter(c => c.id !== cursoId));
                setForm(prev => prev ? { ...prev, cursoIds: (prev.cursoIds ?? []).filter(id => id !== cursoId) } : prev);
            }
        } finally {
            setRemovingCursoBusy(null);
        }
    };

    const getServicoName = (item: any): string | null => {
        if (item?.servicoName) return item.servicoName;
        if (item?.tipoServicoName) return item.tipoServicoName;
        if (item?.tipoServico?.name) return item.tipoServico.name;
        if (item?.TipoServico?.Name) return item.TipoServico.Name;
        if (item?.servicoId || item?.ServicoId || item?.tipoServicoId || item?.TipoServicoId) {
            const id = item.servicoId ?? item.ServicoId ?? item.tipoServicoId ?? item.TipoServicoId;
            return `Serviço #${id}`;
        }
        return null;
    };

    const getServicoValor = (item: any): number | null => {
        const raw =
            item?.valorServico ??
            item?.ValorServico ??
            item?.value ??
            item?.Value ??
            item?.tipoServico?.value ??
            item?.TipoServico?.Value ??
            null;

        return (typeof raw === 'number' && Number.isFinite(raw)) ? raw : null;
    };

    const formatBRL = (n: number) =>
        n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const canSave =
        !!form?.tipoAgendaId &&
        !!form?.startDate &&
        !!form?.endDate &&
        linkedCursos.length > 0;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form) return;

        const cursosEfetivos =
            (form.cursoIds?.length ? form.cursoIds : (form.cursoId != null ? [form.cursoId] : []));

        if (!cursosEfetivos.length) {
            Swal.fire('Atenção', 'Esse evento precisa ter ao menos 1 curso vinculado.', 'warning');
            return;
        }

        const ok = await updateAgenda({
            ...form,
            cursoIds: cursosEfetivos,
        });

        if (ok) {
            onUpdated?.();
            toggle();
        }
    };

    const handleDelete = async () => {
        if (!form) return;

        const result = await Swal.fire({
            title: "Excluir evento?",
            text: "Essa ação não pode ser desfeita.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar",
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        try {
            await deleteAgenda(form.id); // <- não usa ok

            // fecha modal e atualiza SEM depender do retorno
            toggle();
            onDeleted?.();

            await Swal.fire("Excluído!", "O evento foi removido.", "success");
        } catch (e) {
            console.error(e);
            Swal.fire("Erro", "Não foi possível excluir o evento.", "error");
        }
    };


    if (!form) return null;

    return (
        <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="xl">
            <ModalHeader toggle={toggle}>Editar evento</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Tipo</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    name="tipoAgendaId"
                                    value={form.tipoAgendaId}
                                    onChange={onChange}
                                >
                                    {listTipoAgenda.map(t => (
                                        <option key={t.id} value={t.id}>{t.description}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Descrição</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="description"
                                    value={form.description}
                                    onChange={onChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label">Etapa</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    name="step"
                                    value={form.step}
                                    onChange={onChange}
                                >
                                    <option value={1}>Aberto</option>
                                    <option value={2}>Em andamento</option>
                                    <option value={3}>Concluído</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Local</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="location"
                                    value={form.location || ''}
                                    onChange={onChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Responsável</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="responsible"
                                    value={form.responsible || ''}
                                    onChange={onChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label d-block">Cursos</Label>

                                <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                                    <div style={{ minWidth: 280, flex: 1 }}>
                                        <Select
                                            classNamePrefix="select"
                                            options={addableCursoOptions}
                                            onChange={(opt: any) => handleAddCurso(opt)}
                                            placeholder="Adicionar curso…"
                                            isClearable
                                            isSearchable
                                            isDisabled={addingCursoBusy}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                        />
                                    </div>
                                    {addingCursoBusy && <span className="text-muted">adicionando…</span>}
                                </div>

                                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                                    {linkedCursos.length === 0 && (
                                        <span className="text-danger">Nenhum curso vinculado — adicione ao menos 1 para salvar.</span>
                                    )}

                                    {linkedCursos.map(curso => (
                                        <span key={curso.id} className="badge badge-primary mr-2" style={{ fontSize: 13 }}>
                                            {curso.name}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger ml-1"
                                                onClick={() => handleRemoveCurso(curso.id)}
                                                disabled={removingCursoBusy === curso.id}
                                            >
                                                x
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Início</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="datetime-local"
                                    name="startDate"
                                    value={form.startDate.slice(0, 16)}
                                    onChange={onChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Fim</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="datetime-local"
                                    name="endDate"
                                    value={form.endDate.slice(0, 16)}
                                    onChange={onChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Beca</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="becaId"
                                    value={form.beca}
                                    onChange={onChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label d-block">Fotógrafos associados</Label>
                                {listTerceiroByAgenda?.length ? (
                                    <div>
                                        {listTerceiroByAgenda.map((t: any) => {
                                            const servicoName = getServicoName(t);
                                            const servicoValor = getServicoValor(t);
                                            return (
                                                <div key={t.id} className="mb-2">
                                                    <span className="badge badge-primary mr-2">
                                                        {t.id} - {t.name}
                                                    </span>
                                                    <span className="text-muted">
                                                        {servicoName
                                                            ? `• ${servicoName}${servicoValor !== null ? ` — ${formatBRL(servicoValor)}` : ''}`
                                                            : '• Sem serviço'}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <span className="text-muted">Nenhum fotógrafo cadastrado</span>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-between mt-4">
                        <Button color="danger" onClick={handleDelete} type="button">
                            Excluir evento
                        </Button>

                        <div>
                            <Button
                                style={{ background: '#585858', color: '#fff' }}
                                type="submit"
                                disabled={!canSave}
                                title={!canSave ? 'Para salvar, selecione ao menos 1 curso e preencha período/tipo.' : undefined}
                            >
                                Salvar
                            </Button>

                            <Button color="secondary" className="ml-2" onClick={toggle} type="button">
                                Cancelar
                            </Button>
                        </div>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default EditAgendaModal;